import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  CircularProgress,
  List,
  ListItem,
  Paper,
  ClickAwayListener,
  Alert,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

let isGoogleLoaded = false;
let googleLoadListeners = [];

function whenGoogleIsReady(callback) {
  if (isGoogleLoaded) {
    callback();
  } else {
    googleLoadListeners.push(callback);
  }
}

if (typeof window !== "undefined" && !isGoogleLoaded) {
  const checkGoogle = () => {
    if (window.google?.maps?.places) {
      isGoogleLoaded = true;
      googleLoadListeners.forEach((cb) => cb());
      googleLoadListeners = [];
    } else {
      setTimeout(checkGoogle, 100);
    }
  };
  checkGoogle();
}

const GooglePlacesAutocomplete = ({
  label = "Address",
  value,
  onChange,
  onSelect,
  showWarningIfUnconfirmed = false,
  confirmed = false,
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [userEdited, setUserEdited] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [hasSelected, setHasSelected] = useState(!!value);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const mapDivRef = useRef(null);

  // Step 1: Wait for Google Maps to load
  useEffect(() => {
    whenGoogleIsReady(() => {
      if (!autocompleteService.current) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
      }
      setReady(true);
    });
  }, []);

  // Step 2: Create PlacesService only once both Google and ref are ready
  useEffect(() => {
    if (ready && mapDivRef.current && !placesService.current) {
      placesService.current = new window.google.maps.places.PlacesService(
        mapDivRef.current
      );
      setMapReady(true);
    }
  }, [ready, mapDivRef.current]);

  const handleInputChange = (e) => {
    const input = e.target.value;
    setInputValue(input);

    if (!userEdited) setUserEdited(true);

    onChange("");
    setHasSelected(false);

    if (!input.trim()) {
      setPredictions([]);
      return;
    }

    if (!autocompleteService.current) return;

    setLoading(true);
    autocompleteService.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: "ca" },
        types: ["address"],
      },
      (results, status) => {
        setLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setPredictions(results || []);
        } else {
          setPredictions([]);
        }
      }
    );
  };

  const handleSelect = (description, placeId) => {
    setInputValue(description);
    setPredictions([]);
    setHasSelected(true);
    onChange(description);

    if (!placeId || !onSelect) return;

    if (!placesService.current) {
      console.warn("❌ PlacesService not ready. Cannot fetch details yet.");
      return;
    }

    placesService.current.getDetails({ placeId }, (result, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        onSelect(result.formatted_address, result);
      }
    });
  };

  const handleClickAway = () => {
    setPredictions([]);
  };

  const handleClear = () => {
    setInputValue("");
    setPredictions([]);
    setHasSelected(false);
    onChange("");
  };

  if (!ready) return null;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div style={{ position: "relative" }}>
        <div ref={mapDivRef} style={{ display: "none" }} />

        <TextField
          label={label}
          variant="outlined"
          fullWidth
          value={inputValue}
          onChange={handleInputChange}
          autoComplete="off"
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
          }}
          InputProps={{
            endAdornment: (
              <>
                {hasSelected && (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                {loading && <CircularProgress size={18} sx={{ ml: 1 }} />}
              </>
            ),
          }}
        />

        {predictions.length > 0 && (
          <Paper
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 10,
              mt: 0.5,
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: 3,
              maxHeight: 240,
              overflowY: "auto",
            }}>
            <List dense disablePadding>
              {predictions.map(({ description, place_id }) => (
                <ListItem
                  key={place_id}
                  button
                  onClick={() => handleSelect(description, place_id)}
                  sx={{
                    px: 2,
                    py: 1,
                    fontSize: "0.95rem",
                    "&:hover": { backgroundColor: "#FFF0EE" },
                  }}>
                  {description}
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {showWarningIfUnconfirmed &&
          userEdited &&
          inputValue.length > 0 &&
          !confirmed && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select a valid address from the suggestions to continue.
            </Alert>
          )}
      </div>
    </ClickAwayListener>
  );
};

export default GooglePlacesAutocomplete;
